/**
 * The Help area's guides: markdown files bundled at build time and rendered
 * through `../markdown`, the same pipeline the format spec page uses.
 *
 * Adding a guide is adding a `.md` file under `guides/` and one entry here —
 * nothing about the index page, the route, or the rendering changes.
 */

import { BACKBLAZE_GUIDE_SLUG, DELETE_VAULT_GUIDE_SLUG, SECRETS_GUIDE_SLUG } from "@bigbear/shared";
import type { ComponentType } from "react";

import { BackblazeCalculator } from "./BackblazeCalculator";
import { DeleteVaultProviderNoteTable } from "./DeleteVaultProviderNoteTable";
import backblazeB2 from "./guides/backblaze-b2.md?raw";
import deleteAVaultByHand from "./guides/delete-a-vault-by-hand.md?raw";
import recoveryKeyAndPassphrase from "./guides/recovery-key-and-passphrase.md?raw";

export interface Guide {
  /** The route segment under `/help/`. */
  slug: string;
  /** Shown in the Help index and as the guide page's own heading. */
  title: string;
  /** One line, shown under the title in the Help index. */
  summary: string;
  /** The guide's markdown source, as bundled by Vite's `?raw` import. */
  source: string;
  /** A piece of the guide that isn't markdown — the Backblaze guide's cost
   * calculator, say. The guide page renders whatever a guide names here
   * after its article, so a guide that needs one is still just an entry in
   * this list, not a change to the page. */
  extra?: ComponentType;
}

export const GUIDES: Guide[] = [
  {
    slug: BACKBLAZE_GUIDE_SLUG,
    title: "Set up Backblaze B2",
    summary:
      "Create the account, the bucket and the key this app needs, and see what the bill actually looks like.",
    source: backblazeB2,
    extra: BackblazeCalculator,
  },
  {
    slug: SECRETS_GUIDE_SLUG,
    title: "Your recovery key and passphrase",
    summary:
      "What each one is, why the recovery key is the one to keep, and where to put it so a dead PC doesn't take it with it.",
    source: recoveryKeyAndPassphrase,
  },
  {
    slug: DELETE_VAULT_GUIDE_SLUG,
    title: "Delete a vault by hand",
    summary:
      "What to do at the provider's own console for every kind of vault this app supports, and what deleting there actually costs and leaves behind.",
    source: deleteAVaultByHand,
    extra: DeleteVaultProviderNoteTable,
  },
];

export function guideBySlug(slug: string): Guide | undefined {
  return GUIDES.find((guide) => guide.slug === slug);
}
